import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";

/**
 * NavBlockContext
 *
 * Narrowly scoped guard for the small window between a subscription
 * being charged and the customer finishing the post-payment
 * ProjectSetupModal. During that window we don't want stray sidebar /
 * header clicks to nav them away before the placeholder project gets
 * its real name & details — that's the only moment we ever block nav.
 *
 * Consumers (sidebar, header, menu popover) read `isNavBlocked` and
 * render disabled visuals / no-op clicks while blocked. The producer
 * is currently just `ProjectSetupModal`, which calls `blockNav(key)`
 * on mount and `unblockNav(key)` on unmount. Keys let multiple
 * blockers coexist safely if we ever add another narrow flow.
 */
export const NavBlockContext = createContext({
  isNavBlocked: false,
  blockNav: () => {},
  unblockNav: () => {}
});

/**
 * Shared visuals for nav targets that are disabled while nav is blocked.
 * Kept here so the header, sidebar, and menu popover stay in sync instead
 * of each re-declaring the same dimmed / not-allowed treatment.
 */
export const NAV_BLOCKED_STYLE = {
  opacity: 0.4,
  cursor: "not-allowed",
  pointerEvents: "none"
};

export const NAV_BLOCKED_TITLE =
  "Finish or cancel your current flow to navigate";

export const NavBlockProvider = ({ children }) => {
  const [blockers, setBlockers] = useState({});

  const blockNav = useCallback(key => {
    setBlockers(prev => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const unblockNav = useCallback(key => {
    setBlockers(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      isNavBlocked: Object.keys(blockers).length > 0,
      blockNav,
      unblockNav
    }),
    [blockers, blockNav, unblockNav]
  );

  return (
    <NavBlockContext.Provider value={value}>
      {children}
    </NavBlockContext.Provider>
  );
};

export const useNavBlock = () => useContext(NavBlockContext);
